import debug from "debug";
import KeyboardShortcuts from "./KeyboardShorcuts";
const log = debug("app:fn");



/**
 * Tracks global events and forwards them to the EventHub.
 * Handles events like mouse, keyboard, pointer, touch, window resize, and drag-and-drop.
 * @category Utils
 * @subcategory EventManagement
 */
class GlobalEventTracker {
  /**
   * Initializes the GlobalEventTracker instance.
   * @param {Object} experience - The experience object containing the EventHub and other dependencies.
   */
  constructor(experience) {
    this.eventHub = experience.eventHub;
    this.keyboardShortcuts = new KeyboardShortcuts(this.eventHub);
    this.eventListeners = [];
    this.pointerData = {
      isMeasuring: false,
      startX: 0,
      startY: 0,
      movementThreshold: 10, // Movement in pixels
    };
    this.initListeners();
  }

  cleanup() {
    console.log("Cleaning up GlobalEventTracker: Removing all registered event listeners.");
    
    this.removeAllListeners();
    
    console.log("GlobalEventTracker cleanup complete.");
  }

  /**
   * Sets up listeners for various global events.
   */
  initListeners() {
    // Mouse events
    this.addEventListener("click", this.handleClick.bind(this));
    this.addEventListener("dblclick", this.handleDoubleClick.bind(this));
    this.addEventListener("mousedown", this.handleMouseDown.bind(this));
    this.addEventListener("mouseup", this.handleMouseUp.bind(this));
    this.addEventListener("mousemove", this.handleMouseMove.bind(this));
    this.addEventListener("wheel", this.handleWheel.bind(this));
    this.addEventListener("contextmenu", this.handleContextMenu.bind(this));

    //Pointer events
    this.addEventListener("pointerdown", this.handlePointerDown.bind(this));
    this.addEventListener("pointermove", this.handlePointerMove.bind(this));
    this.addEventListener("pointerup", this.handlePointerUp.bind(this));
    this.addEventListener("pointercancel", this.handlePointerCancel.bind(this));

    // Keyboard events
    this.addEventListener(
      "keydown",
      this.keyboardShortcuts.handleKeyDown.bind(this.keyboardShortcuts)
    );
    this.addEventListener("keyup", this.handleKeyUp.bind(this));

    // Touch events
    this.addEventListener("touchstart", this.handleTouchStart.bind(this));
    this.addEventListener("touchmove", this.handleTouchMove.bind(this));
    this.addEventListener("touchend", this.handleTouchEnd.bind(this));
    this.addEventListener("touchcancel", this.handleTouchCancel.bind(this));

    // Window events
    this.addEventListener("resize", this.handleResize.bind(this));
    this.addEventListener("load", this.handleLoad.bind(this));

    // Custom drag-and-drop events
    this.addEventListener("dragstart", this.handleDragStart.bind(this));
    this.addEventListener("drag", this.handleDrag.bind(this));
    this.addEventListener("dragend", this.handleDragEnd.bind(this));
  }

  /**
   * Adds an event listener to the global window object and tracks it.
   * @param {string} event - The event name.
   * @param {Function} handler - The event handler function.
   */
  addEventListener(event, handler) {
    window.addEventListener(event, handler);
    this.eventListeners.push({ event, handler });
  }

  /**
   * Emits a globalLoad event to the EventHub.
   * @param {Event} event - The load event object.
   */
  handleLoad(event) {
    this.eventHub.emit("globalLoad", event);
  }

  /**
   * Emits a globalClick event to the EventHub.
   * @param {MouseEvent} event - The click event object.
   */
  handleClick(event) {
    this.eventHub.emit("globalClick", event);
  }

  /**
   * Emits a globalDoubleClick event to the EventHub.
   * @param {MouseEvent} event - The double-click event object.
   */
  handleDoubleClick(event) {
    this.eventHub.emit("globalDoubleClick", event);
  }

  /**
   * Emits a globalMouseDown event to the EventHub.
   * @param {MouseEvent} event - The mousedown event object.
   */
  handleMouseDown(event) {
    this.eventHub.emit("globalMouseDown", event);
  }

  /**
   * Emits a globalMouseUp event to the EventHub.
   * @param {MouseEvent} event - The mouseup event object.
   */
  handleMouseUp(event) {
    this.eventHub.emit("globalMouseUp", event);
  }

  /**
   * Emits a globalMouseMove event to the EventHub.
   * @param {MouseEvent} event - The mousemove event object.
   */
  handleMouseMove(event) {
    this.eventHub.emit("globalMouseMove", event);
  }

  /**
   * Emits a globalWheel event to the EventHub.
   * @param {WheelEvent} event - The wheel event object.
   */
  handleWheel(event) {
    this.eventHub.emit("globalWheel", event);
  }

  /**
   * Suppresses the native context menu and logs the event.
   * @param {MouseEvent} event - The contextmenu event object.
   */
  handleContextMenu(event) {
    event.preventDefault(); // Suppress native context menu
    log("Contextmenu event captured and suppressed:", event);
    // No emission of globalContextMenu here
  }

  /**
   * Clears the pointer data used for measuring pointer interactions.
   */
  clearPointerData() {
    this.pointerData.isMeasuring = false;
    this.pointerData.startX = 0;
    this.pointerData.startY = 0;
  }

  // Individual pointer event handlers


 
  /**
   * Emits a globalPointerDown event and tracks potential context menu interactions.
   * @param {PointerEvent} event - The pointerdown event object.
   */
  handlePointerDown(event) {
    this.eventHub.emit("globalPointerDown", event);

    // Track context menu candidate only for right mouse button
    if (event.button === 2) {
      this.pointerData.isMeasuring = true;
      this.pointerData.startX = event.clientX;
      this.pointerData.startY = event.clientY;
    }
  }

  /**
   * Emits a globalPointerMove event and tracks pointer movement.
   * @param {PointerEvent} event - The pointermove event object.
   */
  handlePointerMove(event) {
    this.eventHub.emit("globalPointerMove", event);
    if (!this.pointerData.isMeasuring) return;

    const distance = this.calculatePointerDistance(event);

    if (distance > this.pointerData.movementThreshold) {
      this.pointerData.isMeasuring = false;
      log("Movement exceeded threshold. Cancelling context menu tracking.");
      this.eventHub.emit("globalPan");
    }
  }

  /**
   * Emits a globalPointerUp event and handles context menu detection.
   * @param {PointerEvent} event - The pointerup event object.
   */
  handlePointerUp(event) {
    this.eventHub.emit("globalPointerUp", event);
    if (event.button !== 2) return; // Only handle right-clicks

    if (this.pointerData.isMeasuring) {
      log(
        `Pointer up (right button) at (${event.clientX}, ${event.clientY}). Emitting globalContextMenu.`
      );
      this.eventHub.emit("globalContextMenu", event);
      this.clearPointerData(); // Reset tracking state
    }

    log(`Pointer up (right button) at (${event.clientX}, ${event.clientY})`);
  }

  /**
   * Calculates the distance moved by the pointer.
   * @param {PointerEvent} event - The pointermove or pointerup event object.
   * @returns {number} The distance moved in pixels.
   */
  calculatePointerDistance(event) {
    return Math.sqrt(
      Math.pow(event.clientX - this.pointerData.startX, 2) +
        Math.pow(event.clientY - this.pointerData.startY, 2)
    );
  }

  /**
   * Emits a globalPointerCancel event and resets pointer data.
   * @param {PointerEvent} event - The pointercancel event object.
   */
  handlePointerCancel(event) {
    this.eventHub.emit("globalPointerCancel", event);
    if (this.pointerData.isMeasuring) {
      log("Pointer interaction canceled. Resetting measurement.");
      this.clearPointerData(); // Reset tracking state
    }
  }

  /**
   * Emits a globalKeyUp event to the EventHub.
   * @param {KeyboardEvent} event - The keyup event object.
   */
  handleKeyUp(event) {
    this.eventHub.emit("globalKeyUp", event);
  }

  /**
   * Emits a globalTouchStart event to the EventHub.
   * @param {TouchEvent} event - The touchstart event object.
   */
  handleTouchStart(event) {
    this.eventHub.emit("globalTouchStart", event);
  }

  /**
   * Emits a globalTouchMove event to the EventHub.
   * @param {TouchEvent} event - The touchmove event object.
   */
  handleTouchMove(event) {
    this.eventHub.emit("globalTouchMove", event);
  }

  /**
   * Emits a globalTouchEnd event to the EventHub.
   * @param {TouchEvent} event - The touchend event object.
   */
  handleTouchEnd(event) {
    this.eventHub.emit("globalTouchEnd", event);
  }

  /**
   * Emits a globalTouchCancel event to the EventHub.
   * @param {TouchEvent} event - The touchcancel event object.
   */
  handleTouchCancel(event) {
    this.eventHub.emit("globalTouchCancel", event);
  }

  /**
   * Emits a globalResize event to the EventHub.
   * @param {UIEvent} event - The resize event object.
   */
  handleResize(event) {
    this.eventHub.emit("globalResize", event);
  }

  /**
   * Emits a globalDragStart event to the EventHub.
   * @param {DragEvent} event - The dragstart event object.
   */
  handleDragStart(event) {
    this.eventHub.emit("globalDragStart", event);
  }

  /**
   * Emits a globalDrag event to the EventHub.
   * @param {DragEvent} event - The drag event object.
   */
  handleDrag(event) {
    this.eventHub.emit("globalDrag", event);
  }

  /**
   * Emits a globalDragEnd event to the EventHub.
   * @param {DragEvent} event - The dragend event object.
   */
  handleDragEnd(event) {
    this.eventHub.emit("globalDragEnd", event);
  }

  handleKeyDown(event) {
    this.eventHub.emit("globalKeyDown", event);
    if (
      event.key === "Delete" ||
      (event.key === "Backspace" &&
        (
          navigator.userAgentData?.platform ||
          navigator.platform ||
          ""
        ).startsWith("Mac"))
    ) {
      const isEditableElement =
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target.isContentEditable;

      if (!isEditableElement) {
        event.preventDefault(); // Prevent default actions, such as navigating back in browsers
        this.eventHub.emit("deleteKeyPressed", event);
      }
    }
  }

  /**
   * Removes all registered global event listeners.
   */
  removeAllListeners() {
    this.eventListeners.forEach(({ event, handler }) => {
      window.removeEventListener(event, handler);
    });
    this.eventListeners = [];
  }
}

export default GlobalEventTracker;
